/**
 * bulkAction/BulkPrefillModal.jsx
 *
 * Prefill selected KPI questions from the previous financial year.
 *
 * Props:
 *   show, onHide, selectedIds, financialYears, selectedFinancialYearId, onSuccess
 *   blockedIds — selected question ids the current user may not prefill
 *                (not assigned); when non-empty the confirm is blocked
 */
import { useState, useEffect } from "react";
import { apiCall } from "../../../../../_services/apiCall";
import config from "../../../../../config/config";
import { S, BigSpin } from "./Shared";
import { KpiIcon } from "../KpiIcons";

const BulkPrefillModal = ({
  show,
  onHide,
  selectedIds = [],
  financialYears = [],
  selectedFinancialYearId,
  onSuccess,
  type,
  blockedIds = [],
}) => {
  const [currentFY,  setCurrentFY]  = useState(null);
  const [previousFY, setPreviousFY] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [prefilling, setPrefilling] = useState(false);
  const [error,      setError]      = useState("");
  const [success,    setSuccess]    = useState("");

  /* ── resolve current + previous FY on open ── */
  useEffect(() => {
    if (!show || !financialYears.length) return;
    setError("");
    setSuccess("");
    setConfirming(false);

    const sorted = [...financialYears].sort((a, b) => a.id - b.id);
    const idx = sorted.findIndex(
      (y) => String(y.id) === String(selectedFinancialYearId)
    );
    if (idx >= 0) {
      setCurrentFY(sorted[idx]);
      setPreviousFY(idx > 0 ? sorted[idx - 1] : null);
    } else {
      const last = sorted.length - 1;
      setCurrentFY(sorted[last] || null);
      setPreviousFY(sorted[last - 1] || null);
    }
  }, [show, financialYears, selectedFinancialYearId]);

  const handlePrefill = async () => {
    if (blockedIds.length > 0) return; // unassigned questions in selection — never fire
    // close the confirm popup right away — result is reported in the main modal
    setConfirming(false);
    setPrefilling(true);
    setError("");
    setSuccess("");
    try {
      const payload = {
        previousFinancialYearId: Number(previousFY.id),
        currentFinancialYearId:  Number(currentFY.id),
        overwriteAllTypes:       false,
      };
      if (type) payload.type = type;
      if (selectedIds.length > 0) payload.questionIds = selectedIds;

      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}prefillQualitativeFromPreviousYear`,
        {},
        payload,
        "POST",
        false,
        false
      );
      if (isSuccess) {
        const count = data?.overwrittenCount || data?.count || 0;
        setSuccess(
          count > 0
            ? `✅ ${count} question(s) prefilled successfully.`
            : "✅ Data prefilled successfully."
        );
        // let the result show, then run follow-up steps and close
        setTimeout(() => {
          onSuccess?.();
          onHide();
        }, 1600);
      } else {
        setError(data?.data?.message || data?.message || "Prefill failed. Please try again.");
      }
    } catch {
      setError("Server error occurred.");
    } finally {
      setPrefilling(false);
    }
  };

  if (!show) return null;

  /* ── FY card sub-component ── */
  const FYCard = ({ label, fy }) => (
    <div
      style={{
        flex: 1,
        background: "#f8fafc",
        border: "1.5px solid #e2e8f0",
        borderRadius: 8,
        padding: "9px 13px",
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: "#94a3b8",
          textTransform: "uppercase",
          letterSpacing: "0.07em",
          marginBottom: 3,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, color: "#1e293b" }}>
        {fy?.financial_year_value || "—"}
      </div>
    </div>
  );

  return (
    <>
      {/* ── Main Modal ── */}
      <div
        style={S.overlay}
        onClick={(e) => { if (e.target === e.currentTarget && !prefilling) onHide(); }}
      >
        <div style={{ ...S.modal, maxWidth: 460 }}>
          <div style={{
            ...S.header,
            background: "linear-gradient(135deg,#f8faff 0%,#f1f5fd 100%)",
            borderBottom: "1px solid #dbe4fd",
            borderRadius: "14px 14px 0 0",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                background: "var(--accent-3, #eff6ff)",
                border: "1.5px solid var(--accent-2, #bfdbfe)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <KpiIcon name="copy" size={16} color="var(--accent, #2563eb)" />
              </div>
              <span style={{ fontSize: 15.5, fontWeight: 700, color: "#0f172a", letterSpacing: "-0.01em" }}>
                Prefill from Previous Year
              </span>
            </div>
            {!prefilling && (
              <button onClick={onHide} style={S.closeBtn}>✕</button>
            )}
          </div>

          <div style={S.body}>
            {error   && <div style={S.errBox}>{error}</div>}
            {success && <div style={S.successBox}>{success}</div>}
            {prefilling && <BigSpin />}

            {!previousFY ? (
              <div style={S.warnBox}>
                ⚠️ No previous financial year available for{" "}
                <b>{currentFY?.financial_year_value}</b>. Cannot prefill.
              </div>
            ) : (
              <>
                {/* FY arrows display */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 18,
                  }}
                >
                  <FYCard label="Copy From" fy={previousFY} />
                  <span style={{ fontSize: 20, color: "#93c5fd", flexShrink: 0 }}>
                    →
                  </span>
                  <FYCard label="Copy To" fy={currentFY} />
                </div>

                {/* Scope info */}
                {selectedIds.length > 0 ? (
                  <div style={S.infoBox}>
                    <b>{selectedIds.length}</b> KPI(s) selected — only these
                    will be prefilled.
                  </div>
                ) : (
                  <div style={S.warnBox}>
                    No specific questions selected —{" "}
                    <b>all questions</b> will be prefilled.
                  </div>
                )}

                {/* Unassigned questions in selection — block the prefill */}
                {blockedIds.length > 0 && (
                  <div style={S.errBox}>
                    ⚠️ <b>{blockedIds.length}</b> selected question(s) are not
                    assigned to you. Deselect them to prefill.
                  </div>
                )}
              </>
            )}
          </div>

          <div style={S.footer}>
            <button
              onClick={onHide}
              disabled={prefilling}
              style={S.btnSecondary}
            >
              Close
            </button>
            {previousFY && (
              <button
                onClick={() => setConfirming(true)}
                disabled={prefilling || !!success || blockedIds.length > 0}
                style={{ ...S.btnPrimary, background: "var(--ink, #0f172a)" }}
              >
                {prefilling ? "Prefilling…" : "Confirm Prefill"}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Confirm sub-modal ── */}
      {confirming && (
        <div style={{ ...S.overlay, zIndex: 3100 }}>
          <div style={{ ...S.modal, maxWidth: 400 }}>
            <div style={{ ...S.header, borderBottomColor: "#fca5a5" }}>
              <span style={{ ...S.title, color: "#dc2626" }}>
                ⚠️ Are you sure?
              </span>
            </div>

            <div style={S.body}>
              {prefilling ? (
                <BigSpin />
              ) : (
                <div style={S.warnBox}>
                  This will <b>overwrite existing data</b> for{" "}
                  <b>{currentFY?.financial_year_value}</b> and cannot be
                  undone.
                </div>
              )}
            </div>

            {!prefilling && (
              <div style={S.footer}>
                <button
                  onClick={() => setConfirming(false)}
                  style={S.btnSecondary}
                >
                  No, Cancel
                </button>
                <button onClick={handlePrefill} style={S.btnDanger}>
                  Yes, Proceed
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default BulkPrefillModal;